import moment from 'moment'
import TimePicker from 'rc-time-picker'
import 'rc-time-picker/assets/index.css'
import React, { useMemo, useState } from 'react'
import Modal from '../../../components/Modal'
import { getDate, getTimeFromDigit } from '../../../helpers/dateHelper'

const toInputDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

const formatTime12h = (date) => {
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const hour12 = hours % 12 || 12
  const minuteStr = String(minutes).padStart(2, '0')
  return `${hour12}:${minuteStr} ${ampm}`
}

const toMomentTime = (value) => {
  if (!value) return null
  if (moment.isMoment(value)) return value

  const asDate = new Date(value)
  if (!Number.isNaN(asDate.getTime())) {
    return moment(asDate)
  }

  const timeStr = getTimeFromDigit(value)
  const parsed = moment(timeStr, ['h:mm a', 'h:mm A'], true)
  return parsed.isValid() ? parsed : null
}

const getInitialFormData = (demoItem, mode) => {
  const now = moment()
  if (mode === 'end') {
    return {
      date: toInputDate(now.toDate()),
      time: now.clone(),
      reason: '',
      demoId: demoItem._id,
    }
  }

  return {
    date: toInputDate(demoItem.currentDemoDate) || toInputDate(now.toDate()),
    time: toMomentTime(demoItem.currentDemoTime) || now.clone(),
    reason: '',
    demoId: demoItem._id,
  }
}

export default function AdminDemoOverrideModal({ setModal, onClick, demoItem, mode = 'start' }) {
  const isStart = mode === 'start'
  const [data, setData] = useState(() => getInitialFormData(demoItem, mode))

  const startedAtLabel = useMemo(() => {
    if (!demoItem?.startedAt) return null
    const started = new Date(demoItem.startedAt)
    if (Number.isNaN(started.getTime())) return null
    return `${getDate(started)} ${formatTime12h(started)}`
  }, [demoItem?.startedAt])

  return (
    <Modal title={isStart ? 'Admin Start Demo' : 'Admin End Demo'} size="md">
      <form
        onSubmit={(e) =>
          onClick(e, {
            ...data,
            time: data.time ? data.time.format('h:mm a') : '',
          })
        }
      >
        <div className="modal-inner-content">
          <p className="xs-text f-op m-weight mb-md">
            {isStart
              ? 'Override tutor OTP and record when the demo started.'
              : 'Record when the demo ended. Use this if the tutor already started the demo or it needs to be closed manually.'}
          </p>

          {!isStart && startedAtLabel ? (
            <div className="input-group">
              <label>Demo Started At</label>
              <p className="xs-text f-op m-weight caps">{startedAtLabel}</p>
              {demoItem?.adminOverride?.start?.by ? (
                <p className="xxxs-text h-op m-weight">
                  Started by admin: {demoItem.adminOverride.start.by.firstName} {demoItem.adminOverride.start.by.lastName}
                </p>
              ) : (
                <p className="xxxs-text h-op m-weight">Started by tutor</p>
              )}
            </div>
          ) : null}

          <div className="input-group-divided">
            <div>
              <label>{isStart ? 'Start Date*' : 'End Date*'}</label>
              <input
                type="date"
                required
                value={data.date}
                className="text-input"
                onChange={(e) => setData((prev) => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div>
              <label>{isStart ? 'Start Time*' : 'End Time*'}</label>
              <TimePicker
                showSecond={false}
                value={data.time}
                allowEmpty={false}
                className="time-input"
                popupClassName="time-select-popup"
                onChange={(value) => setData((prev) => ({ ...prev, time: value }))}
                minuteStep={15}
                format={'h:mm a'}
                use12Hours
                inputReadOnly
              />
            </div>
          </div>

          <div className="input-group">
            <label>Reason*</label>
            <textarea
              rows={3}
              required
              draggable={false}
              value={data.reason}
              className="text-input"
              style={{ resize: 'none' }}
              placeholder={isStart ? 'Why is this demo being started manually?' : 'Why is this demo being ended manually?'}
              onChange={(e) => setData((prev) => ({ ...prev, reason: e.target.value }))}
            />
          </div>
        </div>

        <div className="modal-footer">
          <input type="button" className="xs-neutral-btn" onClick={() => setModal(false)} value="Cancel" />
          <input
            type="submit"
            className="xs-delete-btn"
            disabled={!data.time || !data.date || !data.reason.trim()}
            value={isStart ? 'Start Demo' : 'End Demo'}
          />
        </div>
      </form>
    </Modal>
  )
}
